// PA-D band computation. Cuts are DATA, read from the tier file's per-subskill
// `bands` object — never hard-coded here (Rhyme Detection's 5/3-4/0-2 differs
// from the standard 4-5/2-3/0-1 table). The JSON spells the third key
// `needsSupport` (camelCase field name); the stored/displayed id is
// `needs_support`, matching the app's Dashboard/PA4K band ids. Do not edit the
// JSON to match the id — the mapping lives here by decision (Step 0 #3).

export const PAD_BAND_IDS = ['mastery', 'emerging', 'needs_support'];

export const PAD_BAND_LABELS = {
  mastery: 'Mastery',
  emerging: 'Emerging',
  needs_support: 'Needs Support',
};

// JSON bands key -> stored band id
const BAND_KEY_TO_ID = {
  mastery: 'mastery',
  emerging: 'emerging',
  needsSupport: 'needs_support',
};

/**
 * @param {number|null|undefined|''} score  administered-items-correct count.
 *   Pass null when zero items were administered — an un-given sub-skill has
 *   no band, and rendering one would invent a result (screener precedent).
 * @param {{mastery:[number,number], emerging:[number,number], needsSupport:[number,number]}} bands
 *   the sub-skill's `bands` object from the tier file, verbatim.
 * @returns {'mastery'|'emerging'|'needs_support'|null} stored band id.
 */
export function computeBand(score, bands) {
  if (score === '' || score === null || score === undefined) return null;
  const n = Number(score);
  if (!Number.isFinite(n) || n < 0) return null; // includes correctCount: -1 (N/A)
  if (!bands) return null;
  for (const key of Object.keys(BAND_KEY_TO_ID)) {
    const range = bands[key];
    if (Array.isArray(range) && n >= range[0] && n <= range[1]) {
      return BAND_KEY_TO_ID[key];
    }
  }
  return null;
}

export function bandLabel(bandId) {
  return PAD_BAND_LABELS[bandId] || null;
}

/**
 * Display-time band for a stored PA-D doc (decision: band is persisted AND
 * recomputed at render; the two must agree for every complete doc). Mirrors
 * the write path exactly: partial docs and zero-administered sub-skills have
 * no band; a discontinued doc keeps the real band of what was administered.
 */
export function displayBandForDoc(docData, subskill) {
  if (!docData || docData.padStatus === 'partial') return null;
  const items = Array.isArray(docData.items) ? docData.items : [];
  const administered = items.filter(
    (r) => r.result === 'correct' || r.result === 'incorrect'
  ).length;
  if (administered === 0) return null;
  return computeBand(docData.correctCount, subskill.bands);
}
